/**
 * stats-table-gen — 康軒數學三下「第 9 單元 統計圖表」之**統計表(表格判讀)**考卷
 * 產生器。對照 `math-exam-gen.ts` 同一套 lockdown 架構(**seeded RNG + 策展閉集模板
 * + 受控變數 + 程式可驗證答案 + validator + 200-seed regression**)。
 *
 * grounded 在康軒三下第 9 章課本真實表格(boss 拍攝),三種表型:
 *   ① priceTable 票價表(報讀 + 計算)——欄=身分/票價(一般觀眾 100 / 學生 70 / 兒童 50
 *     / 65 歲以上 免費)。題:某身分票價?某組合共多少?A 比 B 貴多少?
 *   ② menuTable 目錄表(報讀 + 計算 + 比較)——早餐(蛋餅 50 / 鬆餅 45 …)+ 飲料
 *     (牛奶 25 / 奶茶 20 …)。題:某餐點多少?某餐+某飲共多少?100 元找多少?哪個最貴?
 *   ③ scheduleTable 二維功課表(報讀)——星期一~五 × 第 1~N 節,格內科目。題:星期幾
 *     第幾節上什麼?某科一週上幾節?星期幾有沒有某科?
 *
 * 鐵則 / 設計(對齊 feedback_generator_source_lockdown,不打地鼠):
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:每題的 answer 都從表資料反算(報讀 = 查表、計算 = 加總、
 *     比較 = 比大小、找零 = 付款−花費),`validateStatsTableExam` 重算後比對。
 *   - **數值鎖在三下(G3)合理範圍**:價格 / 金額為非負整數、組合與付款題確保「夠付」
 *     (找零 ≥ 0)、品項在 K-3 合理閉集。表內品項不重複、價格不矛盾。
 *   - **lockdown 防壞題**:模板 = 策展閉集常數(PRICE_TEMPLATES / MENU_TEMPLATES /
 *     SCHEDULE_SUBJECTS);硬規則寫進產生器(夠付、非負、報讀對齊表資料);validator 驗;
 *     200-seed regression 全過(tests/learn/stats-table-gen.test.ts)。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

// ───────────────────────────── 型別 ─────────────────────────────

/** 表型(七種)。 */
export type StatsTableKind =
  | 'price'
  | 'menu'
  | 'schedule'
  | 'quantity'
  | 'transit'
  | 'program-schedule'
  | 'fareMatrix';

/** ① 票價表 / ③ 一維目錄一格 = 一列(身分/品項 → 數值)。 */
export interface TableRow {
  /** 列名(身分 / 品項)。 */
  name: string;
  /** 數值(票價 / 價格,非負整數;免費 = 0)。 */
  value: number;
  /** 顯示用標註(如「免費」取代 0 元);無則直接顯示數值 + 單位。 */
  display?: string;
}

/**
 * ① 票價表資料(`kind:'price'`):一張表 + 一組從表反算的題。
 * 表 = 身分 → 票價(closed set,含一個免費身分讓孩子讀「免費 = 0 元」)。
 */
export interface PriceTableData {
  kind: 'price';
  /** 表標題(如「動物園門票」)。 */
  caption: string;
  /** 欄表頭(['身分', '票價'])。 */
  headers: [string, string];
  /** 各列(身分 → 票價)。 */
  rows: TableRow[];
  /** 數值單位(元)。 */
  unit: string;
}

/**
 * ② 目錄表資料(`kind:'menu'`):兩組(主餐 + 飲料),各是品項 → 價格。
 * 題涵蓋報讀 / 組合加總 / 100 元找零 / 比較(最貴 / 一樣多)。
 */
export interface MenuTableData {
  kind: 'menu';
  caption: string;
  /** 主餐組標題(如「早餐」)。 */
  groupAName: string;
  /** 飲料組標題(如「飲料」)。 */
  groupBName: string;
  /** 主餐品項。 */
  groupA: TableRow[];
  /** 飲料品項。 */
  groupB: TableRow[];
  unit: string;
}

/**
 * ③ 二維功課表資料(`kind:'schedule'`):星期(欄)× 節次(列),格內科目。
 * `grid[節index][星期index]` = 科目名。題涵蓋「星期幾第幾節上什麼」「某科一週幾節」
 * 「星期幾有沒有某科」。
 */
export interface ScheduleTableData {
  kind: 'schedule';
  caption: string;
  /** 星期欄表頭(['星期一'..'星期五'])。 */
  days: string[];
  /** 節次列表頭(['第 1 節'..])。 */
  periods: string[];
  /** grid[periodIdx][dayIdx] = 科目名。 */
  grid: string[][];
}

/**
 * ④ 一維數量統計表(`kind:'quantity'`):欄 = 類別(星期日~六 / 顏色 …),
 * 值 = 數量(瓶 / 個 …)。grounded 在真考卷「美味果汁店一星期賣出的果汁瓶數」。
 * 題涵蓋報讀 / 哪天最多 / 哪天最少 / 某天比某天多賣幾 / 整週共幾。
 * 硬規則:最多 / 最少各唯一(值不重複),供 max / min 題答案唯一。
 */
export interface QuantityTableData {
  kind: 'quantity';
  caption: string;
  /** 類別表頭(['星期日'..'星期六'])。 */
  categories: string[];
  /** 各類別對應數量(非負整數,彼此相異)。 */
  counts: number[];
  /** 數量單位(瓶 / 個 …)。 */
  unit: string;
}

/**
 * ⑤ 時刻表(`kind:'transit'`):二維(車次 × 站名),格內為時間(分鐘表 minutes,
 * 渲染成 hh:mm)或 `null`(過站不停,顯示 `—`)。grounded 在真考卷台鐵 / 高鐵時刻表。
 * 題涵蓋報讀(某車次某站幾點)/ 時間相減(從 A 站到 B 站經過幾時幾分)/
 * 某時間前要到某站可搭哪班車。
 * 硬規則:每車次站時間嚴格遞增(同日 hh:mm)、相減非負、跨時進位由分鐘總量算。
 */
export interface TransitTableData {
  kind: 'transit';
  caption: string;
  /** 車次名(['1101 車次', '1103 車次' …])。 */
  trains: string[];
  /** 站名(['臺北', '板橋' …],由北到南 / 行進方向)。 */
  stations: string[];
  /** grid[trainIdx][stationIdx] = 該站到站時間(minutes since midnight);null = 過站不停。 */
  grid: Array<Array<number | null>>;
}

/**
 * ⑥ 節目單時刻表(`kind:'program-schedule'`):時間(分鐘,遞增)× 節目名。
 * grounded 在真考卷活動 / 節目單。題涵蓋某時間有什麼節目(報讀)/ 某節目多長
 *(到下一個節目的時間差)。硬規則:時間嚴格遞增、長度 = 下一節目時間 − 本節目時間。
 */
export interface ProgramScheduleTableData {
  kind: 'program-schedule';
  caption: string;
  /** 各列開始時間(minutes since midnight,嚴格遞增)。 */
  times: number[];
  /** 各列節目名(與 times 一一對應)。 */
  programs: string[];
}

/**
 * ⑦ 二維票價表(`kind:'fareMatrix'`):起站(列)× 到站(欄),格內票價。
 * grounded 在真考卷台鐵南迴線票價表。題涵蓋某段(A 到 B)票價(報讀)/
 * A 段比 B 段貴多少(相減)。硬規則:票價非負整數、A 到 B 與 B 到 A 同價(對稱)、
 * 同站 = 0、距離越遠越貴(單調)。
 */
export interface FareMatrixTableData {
  kind: 'fareMatrix';
  caption: string;
  /** 站名(['枋寮', '加祿' …],沿線順序)。 */
  stations: string[];
  /** matrix[i][j] = 從站 i 到站 j 的票價(對稱,同站 = 0)。 */
  matrix: number[][];
  unit: string;
}

export type StatsTableData =
  | PriceTableData
  | MenuTableData
  | ScheduleTableData
  | QuantityTableData
  | TransitTableData
  | ProgramScheduleTableData
  | FareMatrixTableData;

/** 一道統計表題:題幹 + 程式可驗證答案 + 反算用 meta(供 validator 重算)。 */
export interface StatsTableQuestion {
  id: string;
  /** 題幹(顯示 / 列印用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。 */
  answer: string;
  /**
   * 反算規格(validator 用):給定所屬表資料,重算出期望答案字串。
   * `op` 決定算法,`args` 是查表 / 計算的索引或鍵。
   */
  verify: StatsVerify;
}

/**
 * 一題的反算規格(closed set)。validator 依 `op` 對表資料重算 → 與 `answer` 比對。
 *   - 'lookup-price'：查票價表第 i 列票價(免費 → '免費')。
 *   - 'sum-price'：票價表多列加總(rows 索引陣列)。
 *   - 'diff-price'：票價表兩列差(|i 列 − j 列|)。
 *   - 'lookup-menu'：查目錄某組某品項價格。
 *   - 'combo-menu'：主餐 i + 飲料 j 加總。
 *   - 'change-menu'：付款 pay − (主餐 i + 飲料 j) 找零。
 *   - 'max-menu'：某組最貴品項名。
 *   - 'equal-menu'：某組兩個價格相同的品項名(排序後 'A、B')。
 *   - 'lookup-cell'：功課表第 p 節星期 d 上什麼。
 *   - 'count-subject'：某科一週上幾節。
 *   - 'has-subject'：星期 d 有沒有某科('有' / '沒有')。
 *   - 'lookup-qty'：一維數量表查第 i 類別數量。
 *   - 'extreme-qty'：哪個類別數量最多 / 最少(類別名,唯一)。
 *   - 'diff-qty'：第 i 類別比第 j 類別多幾(|counts[i] − counts[j]|)。
 *   - 'sum-qty'：整表數量加總。
 *   - 'lookup-transit'：時刻表查第 t 車次第 s 站到站時間(過站不停 → '—')。
 *   - 'elapsed-transit'：第 t 車次從 a 站到 b 站經過時間('X 時 Y 分' / 'Y 分鐘')。
 *   - 'train-before'：要在 cutoff(分)前(含)到第 s 站,最晚可搭哪班車(車次名)。
 *   - 'lookup-program'：節目單第 r 列時間有什麼節目(報讀)。
 *   - 'duration-program'：第 r 個節目多長(下一節目時間 − 本節目時間,'Y 分鐘')。
 *   - 'lookup-fare'：二維票價表從 a 站到 b 站票價。
 *   - 'diff-fare'：(a→b 票價) − (c→d 票價) 差(貴多少;保證非負)。
 */
export type StatsVerify =
  | { op: 'lookup-price'; row: number }
  | { op: 'sum-price'; rows: number[] }
  | { op: 'diff-price'; a: number; b: number }
  | { op: 'lookup-menu'; group: 'A' | 'B'; row: number }
  | { op: 'combo-menu'; a: number; b: number }
  | { op: 'change-menu'; a: number; b: number; pay: number }
  | { op: 'max-menu'; group: 'A' | 'B' }
  | { op: 'equal-menu'; group: 'A' | 'B' }
  | { op: 'lookup-cell'; period: number; day: number }
  | { op: 'count-subject'; subject: string }
  | { op: 'has-subject'; day: number; subject: string }
  | { op: 'lookup-qty'; col: number }
  | { op: 'extreme-qty'; which: 'max' | 'min' }
  | { op: 'diff-qty'; a: number; b: number }
  | { op: 'sum-qty' }
  | { op: 'lookup-transit'; train: number; station: number }
  | { op: 'elapsed-transit'; train: number; a: number; b: number }
  | { op: 'train-before'; station: number; cutoff: number }
  | { op: 'lookup-program'; row: number }
  | { op: 'duration-program'; row: number }
  | { op: 'lookup-fare'; a: number; b: number }
  | { op: 'diff-fare'; a: number; b: number; c: number; d: number };

/** 一大題 = 一張表 + 該表的題目陣列 + 標籤 / 標題 / 配分。 */
export interface StatsTableSection {
  kind: StatsTableKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '票價表'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  /** 該大題的表資料(渲染 / 反算用)。 */
  table: StatsTableData;
  /** 該表的題目。 */
  questions: StatsTableQuestion[];
}

/** 一份統計表考卷。 */
export interface StatsTableExam {
  id: string;
  title: string;
  /** 教科書版本(數學 = 康軒)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: StatsTableSection[];
}

/** 各表型題數(預設見 DEFAULT_COUNTS;key = StatsTableKind)。 */
export type StatsTableCounts = Partial<Record<StatsTableKind, number>>;

/** `generateStatsTableExam` 選項。 */
export interface StatsTableGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各表型題數,預設見 DEFAULT_COUNTS。 */
  counts?: StatsTableCounts;
  /** 卷 id(預設 `stats-table-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `統計表考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── G3 護欄(硬規則)─────────────────────────────

/** 找零題付款金額候選(整百 / 整五十,確保「夠付」後 ≥ 一張表能成立)。 */
const PAY_OPTIONS = [100, 150, 200] as const;

// ───────────────────────────── 策展閉集模板 ─────────────────────────────

/**
 * ① 票價表模板(策展閉集)。每個模板 = 一個情境(caption)+ 4 個身分價格梯度。
 * 價格在 K-3 合理範圍(整十,含一個「免費 = 0」身分,練讀免費)。產生器可在 ±受控
 * 範圍微調數值(見 jitterPrice),但保持梯度與非負。
 */
interface PriceTemplate {
  caption: string;
  /** [身分, 基準價];價格降序、最後一個為 0(免費)。 */
  rows: Array<{ name: string; base: number }>;
}

const PRICE_TEMPLATES: readonly PriceTemplate[] = [
  {
    caption: '動物園門票',
    rows: [
      { name: '一般觀眾', base: 100 },
      { name: '12 歲以上學生', base: 70 },
      { name: '未滿 12 歲兒童', base: 50 },
      { name: '65 歲以上長者', base: 0 },
    ],
  },
  {
    caption: '美術館門票',
    rows: [
      { name: '成人', base: 80 },
      { name: '學生', base: 60 },
      { name: '兒童', base: 40 },
      { name: '65 歲以上長者', base: 0 },
    ],
  },
  {
    caption: '遊樂園門票',
    rows: [
      { name: '一般成人', base: 120 },
      { name: '青少年學生', base: 90 },
      { name: '兒童', base: 60 },
      { name: '滿 65 歲長者', base: 0 },
    ],
  },
];

/**
 * ② 目錄表模板(策展閉集)。主餐 4 品 + 飲料 4 品,各品項一個基準價。
 * 飲料組刻意安排兩品同價(練「哪兩樣一樣多」);主餐組價格互異(練「最貴」)。
 */
interface MenuTemplate {
  caption: string;
  groupAName: string;
  groupBName: string;
  groupA: Array<{ name: string; base: number }>;
  /** 飲料:後兩品同價(equal 題用)。 */
  groupB: Array<{ name: string; base: number }>;
}

const MENU_TEMPLATES: readonly MenuTemplate[] = [
  {
    caption: '早餐店價目表',
    groupAName: '餐點',
    groupBName: '飲料',
    groupA: [
      { name: '蛋餅', base: 50 },
      { name: '鬆餅', base: 45 },
      { name: '魚柳堡', base: 40 },
      { name: '起司菇蛋', base: 30 },
    ],
    groupB: [
      { name: '牛奶', base: 25 },
      { name: '奶茶', base: 20 },
      { name: '豆漿', base: 15 },
      { name: '米漿', base: 15 },
    ],
  },
  {
    caption: '點心店價目表',
    groupAName: '點心',
    groupBName: '飲料',
    groupA: [
      { name: '銅鑼燒', base: 45 },
      { name: '雞蛋糕', base: 35 },
      { name: '車輪餅', base: 25 },
      { name: '蔥油餅', base: 30 },
    ],
    groupB: [
      { name: '紅茶', base: 20 },
      { name: '綠茶', base: 20 },
      { name: '果汁', base: 30 },
      { name: '汽水', base: 25 },
    ],
  },
  {
    caption: '飲料店價目表',
    groupAName: '鬆餅',
    groupBName: '飲品',
    groupA: [
      { name: '草莓鬆餅', base: 55 },
      { name: '巧克力鬆餅', base: 50 },
      { name: '蜂蜜鬆餅', base: 40 },
      { name: '原味鬆餅', base: 35 },
    ],
    groupB: [
      { name: '珍珠奶茶', base: 35 },
      { name: '檸檬紅茶', base: 30 },
      { name: '冬瓜茶', base: 25 },
      { name: '麥茶', base: 25 },
    ],
  },
];

/**
 * ③ 功課表科目閉集(康軒三下實際科目用詞)。產生器從此池填 grid,
 * 保證每科至少出現一次以利「某科一週幾節」「星期幾有沒有某科」題。
 */
const SCHEDULE_SUBJECTS: readonly string[] = [
  '國語',
  '數學',
  '自然',
  '社會',
  '英語',
  '健康',
  '體育',
  '藝術',
  '綜合活動',
  '本土語文',
];

const SCHEDULE_DAYS = ['星期一', '星期二', '星期三', '星期四', '星期五'] as const;
/** 功課表節次(三下一個上午約 4 節;用 5 節讓二維表夠豐富但不爆版)。 */
const SCHEDULE_PERIODS = 5;

/**
 * ④ 一維數量統計表模板(策展閉集)。grounded 在真考卷「一星期賣出的數量」。
 * categories = 星期日~六(7 欄);產生器填數量(整十級距,彼此相異,供最多/最少唯一)。
 */
interface QuantityTemplate {
  caption: string;
  categories: string[];
  unit: string;
  /** 各類別基準數量(供 jitter,長度 = categories.length)。 */
  bases: number[];
}

const QTY_WEEK = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];

const QUANTITY_TEMPLATES: readonly QuantityTemplate[] = [
  {
    caption: '美味果汁店一星期賣出的果汁瓶數',
    categories: QTY_WEEK,
    unit: '瓶',
    bases: [30, 45, 25, 50, 35, 60, 40],
  },
  {
    caption: '小書店一星期賣出的故事書本數',
    categories: QTY_WEEK,
    unit: '本',
    bases: [20, 35, 15, 40, 25, 50, 30],
  },
  {
    caption: '麵包店一星期賣出的麵包個數',
    categories: QTY_WEEK,
    unit: '個',
    bases: [40, 55, 35, 60, 45, 70, 50],
  },
];

/**
 * ⑤ 時刻表模板(策展閉集)。grounded 在真考卷台鐵 / 高鐵時刻表。
 * stations 由行進方向排序;產生器替每車次排一條嚴格遞增的時間鏈,並隨機讓少數中間站
 *「過站不停」(null)。第一站發車時間與站間隔時間受控,確保相減非負、可進位。
 */
interface TransitTemplate {
  caption: string;
  trains: string[];
  stations: string[];
}

const TRANSIT_TEMPLATES: readonly TransitTemplate[] = [
  {
    caption: '高鐵北上時刻表',
    trains: ['1101 車次', '1103 車次', '1105 車次'],
    stations: ['左營', '臺南', '嘉義', '臺中', '新竹', '桃園', '板橋', '臺北'],
  },
  {
    caption: '台鐵自強號時刻表',
    trains: ['301 車次', '303 車次', '305 車次'],
    stations: ['臺北', '板橋', '桃園', '新竹', '臺中', '嘉義', '臺南', '左營'],
  },
];

/**
 * ⑥ 節目單時刻表模板(策展閉集)。grounded 在真考卷活動 / 節目單。
 * 產生器從開場時間開始,各節目給一段時長(受控分鐘),累出嚴格遞增時間鏈。
 */
interface ProgramTemplate {
  caption: string;
  /** 第一個節目開場時間(minutes since midnight)。 */
  startMin: number;
  /** 節目名(依序);每個節目時長由產生器在受控集合抽。 */
  programs: string[];
}

const PROGRAM_TEMPLATES: readonly ProgramTemplate[] = [
  {
    caption: '兒童劇場節目單',
    startMin: 10 * 60, // 10:00
    programs: ['開場表演', '布偶戲', '說故事', '魔術秀', '大合唱'],
  },
  {
    caption: '校慶活動節目單',
    startMin: 9 * 60, // 09:00
    programs: ['升旗典禮', '園遊會', '才藝表演', '趣味競賽', '頒獎典禮'],
  },
];

/** 節目時長候選(分鐘,K-3 合理且可進位:含跨整時的 45/90)。 */
const PROGRAM_DURATIONS = [20, 30, 40, 45, 60, 90] as const;

/**
 * ⑦ 二維票價表模板(策展閉集)。grounded 在真考卷台鐵南迴線票價。
 * stations 沿線順序;產生器依「相鄰站票價(受控)」累加成對稱矩陣(距離越遠越貴)。
 */
interface FareTemplate {
  caption: string;
  stations: string[];
}

const FARE_TEMPLATES: readonly FareTemplate[] = [
  {
    caption: '南迴線票價表',
    stations: ['枋寮', '加祿', '大武', '太麻里', '臺東'],
  },
  {
    caption: '北迴線票價表',
    stations: ['花蓮', '新城', '和平', '南澳', '蘇澳'],
  },
];

/** 相鄰站票價候選(元,整十,供累加成單調對稱矩陣)。 */
const FARE_STEP_OPTIONS = [20, 30, 40, 50] as const;

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 math-exam-gen / 聽力卷同款,純函式可重現)。
 * 注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [min, max] 的整數(含兩端)。 */
function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** 抽一個 [0, n) 的索引。 */
function pickIdx(rng: () => number, n: number): number {
  return Math.floor(rng() * n);
}

/** 抽 k 個相異索引([0, n)),回排序後陣列。 */
function pickDistinct(rng: () => number, n: number, k: number): number[] {
  const pool = Array.from({ length: n }, (_, i) => i);
  const out: number[] = [];
  for (let i = 0; i < k && pool.length > 0; i++) {
    const j = pickIdx(rng, pool.length);
    out.push(pool.splice(j, 1)[0]);
  }
  return out.sort((a, b) => a - b);
}

// ───────────────────────────── 表資料生成 ─────────────────────────────

/**
 * 票價表的價格微調:在基準價上 ±0/10(整十,非負;免費身分恒 0)。
 * 確保價格 ≥ 0、保持降序梯度可被「比 A 貴多少」算出非負差。
 */
function jitterPrice(rng: () => number, base: number): number {
  if (base === 0) return 0;
  const delta = pick(rng, [0, 10, -10]);
  const v = base + delta;
  return Math.max(10, v);
}

/** 顯示字串:0 → '免費';否則 `N 元`。 */
function priceDisplay(value: number, unit: string): string {
  return value === 0 ? '免費' : `${value} ${unit}`;
}

function buildPriceTable(rng: () => number): PriceTableData {
  const tpl = pick(rng, PRICE_TEMPLATES);
  const unit = '元';
  const rows: TableRow[] = tpl.rows.map((r) => {
    const value = jitterPrice(rng, r.base);
    return { name: r.name, value, display: value === 0 ? '免費' : undefined };
  });
  return {
    kind: 'price',
    caption: tpl.caption,
    headers: ['身分', '票價'],
    rows,
    unit,
  };
}

function buildMenuTable(rng: () => number): MenuTableData {
  const tpl = pick(rng, MENU_TEMPLATES);
  const unit = '元';
  // 主餐微調 ±0/5,保持互異(供「最貴」唯一);飲料保持後兩品同價(供 equal 題)。
  const jitterMenu = (base: number): number => Math.max(10, base + pick(rng, [0, 5, -5]));
  const groupA: TableRow[] = [];
  const usedA = new Set<number>();
  for (const item of tpl.groupA) {
    let v = jitterMenu(item.base);
    while (usedA.has(v)) v += 5; // 保持主餐價格互異(最貴唯一)。
    usedA.add(v);
    groupA.push({ name: item.name, value: v });
  }
  // 飲料:後兩品同價(equal 題**唯一一組**);前面品項彼此互異且不撞 equal 值
  // (硬規則:整組恰一對同價,讓「哪兩樣一樣多」答案唯一)。
  const groupB: TableRow[] = [];
  const equalValue = jitterMenu(tpl.groupB[tpl.groupB.length - 1].base);
  const usedB = new Set<number>([equalValue]);
  for (let i = 0; i < tpl.groupB.length; i++) {
    const item = tpl.groupB[i];
    if (i >= tpl.groupB.length - 2) {
      groupB.push({ name: item.name, value: equalValue });
    } else {
      let v = jitterMenu(item.base);
      while (usedB.has(v)) v += 5; // 前面品項不可撞 equal 值,也不可彼此相同。
      usedB.add(v);
      groupB.push({ name: item.name, value: v });
    }
  }
  return {
    kind: 'menu',
    caption: tpl.caption,
    groupAName: tpl.groupAName,
    groupBName: tpl.groupBName,
    groupA,
    groupB,
    unit,
  };
}

function buildScheduleTable(rng: () => number): ScheduleTableData {
  const days = [...SCHEDULE_DAYS];
  const periods = Array.from({ length: SCHEDULE_PERIODS }, (_, i) => `第 ${i + 1} 節`);
  const totalCells = SCHEDULE_PERIODS * days.length;
  // 先保證五個核心科目(國/數/自/社/英)各至少出現一次,其餘格從全池填。
  const core = ['國語', '數學', '自然', '社會', '英語'];
  const flat: string[] = [];
  for (const s of core) flat.push(s);
  while (flat.length < totalCells) flat.push(pick(rng, SCHEDULE_SUBJECTS));
  // 洗牌(Fisher–Yates,seeded)。
  for (let i = flat.length - 1; i > 0; i--) {
    const j = pickIdx(rng, i + 1);
    [flat[i], flat[j]] = [flat[j], flat[i]];
  }
  const grid: string[][] = [];
  let k = 0;
  for (let p = 0; p < SCHEDULE_PERIODS; p++) {
    const row: string[] = [];
    for (let d = 0; d < days.length; d++) row.push(flat[k++]);
    grid.push(row);
  }
  return { kind: 'schedule', caption: '一週功課表', days, periods, grid };
}

// ───────────────────────────── 時間工具(hh:mm ↔ 分鐘)─────────────────────────────

/** 分鐘(since midnight)→ 'hh:mm'(補零,如 540 → '09:00')。 */
export function formatHM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * 兩個時間相減(分鐘總量)→ 人類可讀經過時間字串(用程式算可驗證、跨時進位正確)。
 *   - < 60 分 → 'Y 分鐘'(如 45 → '45 分鐘')。
 *   - 整時 → 'X 時'(如 120 → '2 時')。
 *   - 否則 → 'X 時 Y 分'(如 150 → '2 時 30 分')。
 * 規格:diff 必為非負(caller 保證 later − earlier)。
 */
export function formatElapsed(diffMin: number): string {
  if (diffMin < 60) return `${diffMin} 分鐘`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m === 0 ? `${h} 時` : `${h} 時 ${m} 分`;
}

// ───────────────────────────── 表資料生成(新四型)─────────────────────────────

function buildQuantityTable(rng: () => number): QuantityTableData {
  const tpl = pick(rng, QUANTITY_TEMPLATES);
  // 數量微調 ±0/5(整五,非負),並保證彼此相異(最多/最少唯一)。
  const used = new Set<number>();
  const counts: number[] = [];
  for (const base of tpl.bases) {
    let v = Math.max(5, base + pick(rng, [0, 5, -5]));
    while (used.has(v)) v += 5;
    used.add(v);
    counts.push(v);
  }
  return {
    kind: 'quantity',
    caption: tpl.caption,
    categories: [...tpl.categories],
    counts,
    unit: tpl.unit,
  };
}

function buildTransitTable(rng: () => number): TransitTableData {
  const tpl = pick(rng, TRANSIT_TEMPLATES);
  const nStations = tpl.stations.length;
  const grid: Array<Array<number | null>> = [];
  for (let t = 0; t < tpl.trains.length; t++) {
    // 每車次:第一站發車 06:00~09:30(整十分),站間隔 15/20/25/30 分(整時鏈遞增)。
    let cur = randInt(rng, 6 * 60, 9 * 60 + 30);
    cur -= cur % 10;
    const row: Array<number | null> = [];
    for (let s = 0; s < nStations; s++) {
      row.push(cur);
      cur += pick(rng, [15, 20, 25, 30]);
    }
    // 過站不停:隨機讓 0~1 個「中間站」(非首末、非相鄰於計算題用站)變 null。
    // 為確保 elapsed 題與 lookup 題可出,只挖一個中間站,且保留首末。
    if (nStations >= 4 && rng() < 0.6) {
      const skip = randInt(rng, 1, nStations - 2);
      row[skip] = null;
    }
    grid.push(row);
  }
  return {
    kind: 'transit',
    caption: tpl.caption,
    trains: [...tpl.trains],
    stations: [...tpl.stations],
    grid,
  };
}

function buildProgramScheduleTable(rng: () => number): ProgramScheduleTableData {
  const tpl = pick(rng, PROGRAM_TEMPLATES);
  const times: number[] = [];
  let cur = tpl.startMin;
  // 每個節目開始時間 = 前一個 + 前一個時長;最後再多排一個「結束」時刻供算最後節目時長。
  for (let i = 0; i < tpl.programs.length; i++) {
    times.push(cur);
    cur += pick(rng, PROGRAM_DURATIONS);
  }
  // 末項哨兵:結束時間(供最後節目算時長),不對外當節目,存進 times 但 programs 補「結束」。
  times.push(cur);
  const programs = [...tpl.programs, '結束'];
  return {
    kind: 'program-schedule',
    caption: tpl.caption,
    times,
    programs,
  };
}

function buildFareMatrixTable(rng: () => number): FareMatrixTableData {
  const tpl = pick(rng, FARE_TEMPLATES);
  const n = tpl.stations.length;
  // 相鄰站票價(受控,整十);累加成「站 i 到站 j = Σ 相鄰票價」對稱矩陣。
  const steps: number[] = [];
  for (let i = 0; i < n - 1; i++) steps.push(pick(rng, FARE_STEP_OPTIONS));
  // 前綴和:dist[i] = 從站 0 到站 i 的累積票價。
  const prefix: number[] = [0];
  for (let i = 0; i < n - 1; i++) prefix.push(prefix[i] + steps[i]);
  const matrix: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    for (let j = 0; j < n; j++) row.push(Math.abs(prefix[j] - prefix[i]));
    matrix.push(row);
  }
  return {
    kind: 'fareMatrix',
    caption: tpl.caption,
    stations: [...tpl.stations],
    matrix,
    unit: '元',
  };
}

// ───────────────────────────── 題目生成(從表反算)─────────────────────────────

/** 把一組值加總。 */
function sumValues(rows: TableRow[], idxs: number[]): number {
  return idxs.reduce((s, i) => s + rows[i].value, 0);
}

/**
 * ① 票價表的題:報讀(某身分票價)/ 組合加總(2~3 人不同身分)/ 兩身分差。
 * 題型循環確保涵蓋三類;答案全從表反算。
 */
function genPriceQuestions(
  rng: () => number,
  table: PriceTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  const rows = table.rows;
  const unit = table.unit;
  // 非免費列(供組合 / 差題,避免「免費 − 免費」這種無意義題)。
  const paidIdx = rows.map((_, i) => i).filter((i) => rows[i].value > 0);
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // 報讀:某身分票價。
      const r = pickIdx(rng, rows.length);
      out.push({
        id: `p${i + 1}`,
        prompt: `「${rows[r].name}」的票價是多少?(免費請寫「免費」)`,
        answer: priceDisplay(rows[r].value, unit),
        verify: { op: 'lookup-price', row: r },
      });
    } else if (variant === 1) {
      // 組合加總:2~3 個不同身分各買一張。
      const k = randInt(rng, 2, Math.min(3, rows.length));
      const idxs = pickDistinct(rng, rows.length, k);
      const total = sumValues(rows, idxs);
      const names = idxs.map((j) => rows[j].name).join(' + ');
      out.push({
        id: `p${i + 1}`,
        prompt: `${names} 各買 1 張票,一共要付多少 ${unit}?`,
        answer: `${total} ${unit}`,
        verify: { op: 'sum-price', rows: idxs },
      });
    } else {
      // 比較:A 比 B 貴多少(取兩個付費身分,確保差 ≥ 0)。
      const [a, b] = pickDistinct(rng, paidIdx.length, 2).map((j) => paidIdx[j]);
      const hi = rows[a].value >= rows[b].value ? a : b;
      const lo = hi === a ? b : a;
      const diff = rows[hi].value - rows[lo].value;
      out.push({
        id: `p${i + 1}`,
        prompt: `「${rows[hi].name}」的票價比「${rows[lo].name}」貴多少 ${unit}?`,
        answer: `${diff} ${unit}`,
        verify: { op: 'diff-price', a: hi, b: lo },
      });
    }
  }
  return out;
}

/**
 * ② 目錄表的題:報讀 / 組合(主餐+飲料)/ 找零(付 100/150/200)/ 比較(最貴 / 一樣多)。
 */
function genMenuQuestions(
  rng: () => number,
  table: MenuTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  const unit = table.unit;
  for (let i = 0; i < n; i++) {
    const variant = i % 4;
    if (variant === 0) {
      // 報讀:某組某品項價格。
      const group: 'A' | 'B' = rng() < 0.5 ? 'A' : 'B';
      const rows = group === 'A' ? table.groupA : table.groupB;
      const r = pickIdx(rng, rows.length);
      out.push({
        id: `m${i + 1}`,
        prompt: `「${rows[r].name}」要多少 ${unit}?`,
        answer: `${rows[r].value} ${unit}`,
        verify: { op: 'lookup-menu', group, row: r },
      });
    } else if (variant === 1) {
      // 組合:一份主餐 + 一杯飲料。
      const a = pickIdx(rng, table.groupA.length);
      const b = pickIdx(rng, table.groupB.length);
      const total = table.groupA[a].value + table.groupB[b].value;
      out.push({
        id: `m${i + 1}`,
        prompt: `買一份「${table.groupA[a].name}」和一杯「${table.groupB[b].name}」,一共要多少 ${unit}?`,
        answer: `${total} ${unit}`,
        verify: { op: 'combo-menu', a, b },
      });
    } else if (variant === 2) {
      // 找零:付款確保「夠付」(pay ≥ combo);選最小可付的整額。
      const a = pickIdx(rng, table.groupA.length);
      const b = pickIdx(rng, table.groupB.length);
      const cost = table.groupA[a].value + table.groupB[b].value;
      const pay = PAY_OPTIONS.find((p) => p >= cost) ?? PAY_OPTIONS[PAY_OPTIONS.length - 1];
      const change = pay - cost;
      out.push({
        id: `m${i + 1}`,
        prompt: `用 ${pay} ${unit}買一份「${table.groupA[a].name}」和一杯「${table.groupB[b].name}」,可以找回多少 ${unit}?`,
        answer: `${change} ${unit}`,
        verify: { op: 'change-menu', a, b, pay },
      });
    } else {
      // 比較:某組哪一樣最貴 / 飲料組哪兩樣一樣多(交替)。
      if (i % 8 === 3) {
        // equal 題只對飲料組(模板保證後兩品同價,且唯一一組)。
        const names = equalNames(table.groupB);
        out.push({
          id: `m${i + 1}`,
          prompt: `「${table.groupBName}」裡面,哪兩樣的價錢一樣多?`,
          answer: names,
          verify: { op: 'equal-menu', group: 'B' },
        });
      } else {
        // max 題對主餐組(模板保證互異,最貴唯一)。
        const name = maxName(table.groupA);
        out.push({
          id: `m${i + 1}`,
          prompt: `「${table.groupAName}」裡面,哪一樣最貴?`,
          answer: name,
          verify: { op: 'max-menu', group: 'A' },
        });
      }
    }
  }
  return out;
}

/** 某組最貴品項名(唯一,模板保證)。 */
function maxName(rows: TableRow[]): string {
  let best = rows[0];
  for (const r of rows) if (r.value > best.value) best = r;
  return best.name;
}

/** 某組兩個同價品項名(排序後 '名1、名2');無同價回 ''(模板保證有)。 */
function equalNames(rows: TableRow[]): string {
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      if (rows[i].value === rows[j].value) {
        return [rows[i].name, rows[j].name].sort().join('、');
      }
    }
  }
  return '';
}

/**
 * ③ 功課表的題:報讀(星期幾第幾節)/ 某科一週幾節 / 星期幾有沒有某科。
 */
function genScheduleQuestions(
  rng: () => number,
  table: ScheduleTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // 報讀:星期 d 第 p 節上什麼。
      const p = pickIdx(rng, table.periods.length);
      const d = pickIdx(rng, table.days.length);
      out.push({
        id: `s${i + 1}`,
        prompt: `${table.days[d]}的${table.periods[p]}上什麼課?`,
        answer: table.grid[p][d],
        verify: { op: 'lookup-cell', period: p, day: d },
      });
    } else if (variant === 1) {
      // 某科一週幾節(用 grid 內真實出現的科目,確保 ≥ 1)。
      const subj = pickPresentSubject(rng, table);
      const count = countSubject(table, subj);
      out.push({
        id: `s${i + 1}`,
        prompt: `這個星期一共上了幾節「${subj}」課?`,
        answer: `${count} 節`,
        verify: { op: 'count-subject', subject: subj },
      });
    } else {
      // 星期 d 有沒有某科(科目取自全池,答案可能 '有' / '沒有')。
      const d = pickIdx(rng, table.days.length);
      const subj = pick(rng, SCHEDULE_SUBJECTS);
      const has = table.grid.some((row) => row[d] === subj);
      out.push({
        id: `s${i + 1}`,
        prompt: `${table.days[d]}有沒有「${subj}」課?(請寫「有」或「沒有」)`,
        answer: has ? '有' : '沒有',
        verify: { op: 'has-subject', day: d, subject: subj },
      });
    }
  }
  return out;
}

/** grid 內真實出現過的科目(供 count 題,確保 ≥ 1 節)。 */
function pickPresentSubject(rng: () => number, table: ScheduleTableData): string {
  const present = new Set<string>();
  for (const row of table.grid) for (const c of row) present.add(c);
  const arr = [...present];
  return arr[pickIdx(rng, arr.length)];
}

/** 某科在整張功課表出現幾次(= 一週幾節)。 */
function countSubject(table: ScheduleTableData, subject: string): number {
  let c = 0;
  for (const row of table.grid) for (const cell of row) if (cell === subject) c += 1;
  return c;
}

/**
 * ④ 一維數量表的題:報讀 / 哪天最多 / 哪天最少 / 某天比某天多幾 / 整週共幾。
 */
function genQuantityQuestions(
  rng: () => number,
  table: QuantityTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  const { categories, counts, unit } = table;
  // 變體順序:報讀 → 相減 → 加總 → 最多 → 最少。
  // 把「相減」「加總」排進前 4,確保預設(4 題)涵蓋真考卷招牌「相減 + 整週加總」。
  for (let i = 0; i < n; i++) {
    const variant = i % 5;
    if (variant === 0) {
      // 報讀:某類別數量。
      const c = pickIdx(rng, categories.length);
      out.push({
        id: `q${i + 1}`,
        prompt: `「${categories[c]}」賣出了幾 ${unit}?`,
        answer: `${counts[c]} ${unit}`,
        verify: { op: 'lookup-qty', col: c },
      });
    } else if (variant === 1) {
      // 某天比某天多幾(取兩個相異類別,確保差 ≥ 0:大減小)。
      const [x, y] = pickDistinct(rng, categories.length, 2);
      const hi = counts[x] >= counts[y] ? x : y;
      const lo = hi === x ? y : x;
      const diff = counts[hi] - counts[lo];
      out.push({
        id: `q${i + 1}`,
        prompt: `「${categories[hi]}」比「${categories[lo]}」多賣了幾 ${unit}?`,
        answer: `${diff} ${unit}`,
        verify: { op: 'diff-qty', a: hi, b: lo },
      });
    } else if (variant === 2) {
      // 整週共幾(全部加總)。
      const total = counts.reduce((s, v) => s + v, 0);
      out.push({
        id: `q${i + 1}`,
        prompt: `這一個星期一共賣出了幾 ${unit}?`,
        answer: `${total} ${unit}`,
        verify: { op: 'sum-qty' },
      });
    } else if (variant === 3) {
      // 哪天最多(值唯一)。
      out.push({
        id: `q${i + 1}`,
        prompt: `哪一天賣出最多?`,
        answer: extremeCategory(table, 'max'),
        verify: { op: 'extreme-qty', which: 'max' },
      });
    } else {
      // 哪天最少(值唯一)。
      out.push({
        id: `q${i + 1}`,
        prompt: `哪一天賣出最少?`,
        answer: extremeCategory(table, 'min'),
        verify: { op: 'extreme-qty', which: 'min' },
      });
    }
  }
  return out;
}

/** 一維數量表最多 / 最少的類別名(值唯一,模板 + jitter 保證)。 */
function extremeCategory(table: QuantityTableData, which: 'max' | 'min'): string {
  let best = 0;
  for (let i = 1; i < table.counts.length; i++) {
    if (which === 'max' ? table.counts[i] > table.counts[best] : table.counts[i] < table.counts[best]) {
      best = i;
    }
  }
  return table.categories[best];
}

/**
 * ⑤ 時刻表的題:報讀(某車次某站幾點)/ 經過時間(某車次 A→B 站相減)/
 * 某時間前要到某站可搭哪班車。每題答案從 grid(分鐘)反算可驗。
 */
function genTransitQuestions(
  rng: () => number,
  table: TransitTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  const { trains, stations, grid } = table;
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // 報讀:某車次某站幾點(挑一個有停的站,過站不停則答 '—')。
      const t = pickIdx(rng, trains.length);
      const s = pickIdx(rng, stations.length);
      const v = grid[t][s];
      out.push({
        id: `t${i + 1}`,
        prompt: `搭「${trains[t]}」,在「${stations[s]}」站是幾點?(過站不停請寫「—」)`,
        answer: v === null ? '—' : formatHM(v),
        verify: { op: 'lookup-transit', train: t, station: s },
      });
    } else if (variant === 1) {
      // 經過時間:某車次從 A 站到 B 站(兩站都要有停,A 早於 B)。
      const t = pickIdx(rng, trains.length);
      const stops = grid[t]
        .map((v, idx) => (v === null ? -1 : idx))
        .filter((idx) => idx >= 0);
      // stops 至少 2(模板挖洞最多 1,且首末保留)→ 取兩個相異,排序。
      const [ia, ib] = pickDistinct(rng, stops.length, 2);
      const a = stops[ia];
      const b = stops[ib];
      const early = a < b ? a : b;
      const late = a < b ? b : a;
      const diff = (grid[t][late] as number) - (grid[t][early] as number);
      out.push({
        id: `t${i + 1}`,
        prompt: `搭「${trains[t]}」,從「${stations[early]}」站到「${stations[late]}」站,要經過多少時間?`,
        answer: formatElapsed(diff),
        verify: { op: 'elapsed-transit', train: t, a: early, b: late },
      });
    } else {
      // 某時間前要到某站可搭哪班車:取一個目標站 + 一個截止時間,答最晚能準時到的車次名。
      // 為答案唯一,挑「該站各車次到站時間皆相異」的站;截止時間 = 某班的到站時間(含)。
      const sCandidates = stations
        .map((_, s) => s)
        .filter((s) => {
          const arrivals = grid.map((row) => row[s]).filter((v): v is number => v !== null);
          return new Set(arrivals).size === arrivals.length && arrivals.length >= 1;
        });
      const s = sCandidates.length > 0 ? pick(rng, sCandidates) : pickIdx(rng, stations.length);
      const arrivals = grid
        .map((row, t) => ({ t, v: row[s] }))
        .filter((x): x is { t: number; v: number } => x.v !== null)
        .sort((p, qq) => p.v - qq.v);
      // 截止時間 = 中間某班的到站時間(含)→ 答「最晚但不超過截止」的車次。
      const cutIdx = randInt(rng, 0, arrivals.length - 1);
      const cutoff = arrivals[cutIdx].v;
      out.push({
        id: `t${i + 1}`,
        prompt: `想在 ${formatHM(cutoff)} (含)以前到「${stations[s]}」站,最晚可以搭哪一班車?`,
        answer: trainBefore(table, s, cutoff) ?? trains[arrivals[cutIdx].t],
        verify: { op: 'train-before', station: s, cutoff },
      });
    }
  }
  return out;
}

/**
 * 「要在 cutoff(含)前到第 s 站,最晚可搭哪班車」的車次名(唯一,挑站時保證該站到站時間相異)。
 * 取「到站時間 ≤ cutoff 且最大」的車次;無符合(理論上不會,因 cutoff = 某班到站時間)回 null。
 */
function trainBefore(table: TransitTableData, station: number, cutoff: number): string | null {
  let bestTrain = -1;
  let bestTime = -1;
  for (let t = 0; t < table.grid.length; t++) {
    const v = table.grid[t][station];
    if (v === null || v === undefined) continue;
    if (v <= cutoff && v > bestTime) {
      bestTime = v;
      bestTrain = t;
    }
  }
  return bestTrain >= 0 ? table.trains[bestTrain] : null;
}

/**
 * ⑥ 節目單的題:某時間有什麼節目(報讀)/ 某節目多長(到下一節目時間差)。
 * 末項是哨兵「結束」,不對它出報讀 / 時長題。
 */
function genProgramQuestions(
  rng: () => number,
  table: ProgramScheduleTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  // 真節目索引 = 0..length-2(末項為「結束」哨兵)。
  const realCount = table.programs.length - 1;
  for (let i = 0; i < n; i++) {
    const variant = i % 2;
    if (variant === 0) {
      // 報讀:某時間有什麼節目。
      const r = pickIdx(rng, realCount);
      out.push({
        id: `g${i + 1}`,
        prompt: `${formatHM(table.times[r])} 開始的是什麼節目?`,
        answer: table.programs[r],
        verify: { op: 'lookup-program', row: r },
      });
    } else {
      // 時長:某節目多長(下一列時間 − 本列時間)。
      const r = pickIdx(rng, realCount);
      const dur = table.times[r + 1] - table.times[r];
      out.push({
        id: `g${i + 1}`,
        prompt: `「${table.programs[r]}」這個節目進行了多久?`,
        answer: formatElapsed(dur),
        verify: { op: 'duration-program', row: r },
      });
    }
  }
  return out;
}

/**
 * ⑦ 二維票價表的題:某段(A→B)票價(報讀)/ A 段比 B 段貴多少(相減,非負)。
 */
function genFareQuestions(
  rng: () => number,
  table: FareMatrixTableData,
  n: number,
): StatsTableQuestion[] {
  const out: StatsTableQuestion[] = [];
  const { stations, matrix, unit } = table;
  const nS = stations.length;
  for (let i = 0; i < n; i++) {
    const variant = i % 2;
    if (variant === 0) {
      // 報讀:從 A 站到 B 站票價(相異兩站)。
      const [a, b] = pickDistinct(rng, nS, 2);
      out.push({
        id: `f${i + 1}`,
        prompt: `從「${stations[a]}」站搭到「${stations[b]}」站,票價是多少 ${unit}?`,
        answer: `${matrix[a][b]} ${unit}`,
        verify: { op: 'lookup-fare', a, b },
      });
    } else {
      // 比較:A 段比 B 段貴多少(取兩段,大段在前確保差 ≥ 0)。
      const [a, b] = pickDistinct(rng, nS, 2);
      const [c, d] = pickDistinct(rng, nS, 2);
      const fare1 = matrix[a][b];
      const fare2 = matrix[c][d];
      // 大段當「貴的那段」放前面,確保差非負且題意通順。
      const hi = fare1 >= fare2 ? { a, b } : { a: c, b: d };
      const lo = fare1 >= fare2 ? { a: c, b: d } : { a, b };
      const diff = matrix[hi.a][hi.b] - matrix[lo.a][lo.b];
      out.push({
        id: `f${i + 1}`,
        prompt: `「${stations[hi.a]}→${stations[hi.b]}」的票價比「${stations[lo.a]}→${stations[lo.b]}」貴多少 ${unit}?`,
        answer: `${diff} ${unit}`,
        verify: { op: 'diff-fare', a: hi.a, b: hi.b, c: lo.a, d: lo.b },
      });
    }
  }
  return out;
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各表型題數(預設)。七大題(grounded 在真考卷題型),Σ(pointsEach×count):
 *   quantity 4×4=16(一維數量:報讀/最多最少/相減/加總)/ price 4×4=16(票價報讀+組合+比較)/
 *   menu 4×4=16(價目:組合+找零)/ fareMatrix 4×4=16(二維票價:報讀+貴多少)/
 *   transit 4×4=16(時刻表:報讀+時間相減,招牌)/ program 4×3=12(節目單:報讀+時長)/
 *   schedule 4×4=16(二維功課表)→ 108。配分非段考 100,是「表格判讀 + 時間計算」專卷
 *  (題量充足、涵蓋真考卷七表型 + 各表型多題種)。
 */
const DEFAULT_COUNTS: Required<StatsTableCounts> = {
  quantity: 4,
  price: 4,
  menu: 4,
  fareMatrix: 4,
  transit: 4,
  'program-schedule': 3,
  schedule: 4,
};

/**
 * 固定出題順序(由淺入深):一維數量 → 票價 → 價目 → 二維票價 → 時刻表(時間) →
 * 節目單(時間) → 二維功課表。涵蓋真考卷招牌:一維數量+加總、時刻表+時間計算、二維票價。
 */
const SECTION_ORDER: StatsTableKind[] = [
  'quantity',
  'price',
  'menu',
  'fareMatrix',
  'transit',
  'program-schedule',
  'schedule',
];

const SECTION_META: Record<
  StatsTableKind,
  { title: string; instructions: string; pointsEach: number }
> = {
  quantity: {
    title: '數量統計表',
    instructions: '看下面的數量統計表回答問題。報讀與計算都要寫單位。每題 4 分。',
    pointsEach: 4,
  },
  price: {
    title: '票價表',
    instructions: '看下面的票價表回答問題。報讀與計算都要寫單位(元)。每題 4 分。',
    pointsEach: 4,
  },
  menu: {
    title: '價目表',
    instructions:
      '看下面的價目表回答問題。組合與找零題要先列式再算,記得寫單位(元)。每題 4 分。',
    pointsEach: 4,
  },
  fareMatrix: {
    title: '票價表(二維表)',
    instructions: '看下面的票價表回答問題。橫的看到站、直的看起站,交叉處就是票價。每題 4 分。',
    pointsEach: 4,
  },
  transit: {
    title: '時刻表',
    instructions:
      '看下面的時刻表回答問題。「—」表示過站不停。算經過時間要注意跨過整點的進位。每題 4 分。',
    pointsEach: 4,
  },
  'program-schedule': {
    title: '節目單時刻表',
    instructions: '看下面的節目單回答問題。節目長度 = 下一個節目開始時間減掉這個節目開始時間。每題 4 分。',
    pointsEach: 4,
  },
  schedule: {
    title: '功課表(二維表)',
    instructions: '看下面的功課表回答問題。橫的看星期、直的看節次。每題 4 分。',
    pointsEach: 4,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];

/**
 * 從策展閉集模板生一份統計表考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / id / title。
 * @returns 一份 `StatsTableExam`(預設三大題:票價 / 價目 / 功課表)。
 *   生成後建議過 `validateStatsTableExam`。
 */
export function generateStatsTableExam(opts: StatsTableGenOpts): StatsTableExam {
  const rng = makeRng(opts.seed);
  const counts: Required<StatsTableCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const sections: StatsTableSection[] = SECTION_ORDER.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const n = counts[kind];
    let table: StatsTableData;
    let questions: StatsTableQuestion[];
    if (kind === 'price') {
      const t = buildPriceTable(rng);
      table = t;
      questions = genPriceQuestions(rng, t, n);
    } else if (kind === 'menu') {
      const t = buildMenuTable(rng);
      table = t;
      questions = genMenuQuestions(rng, t, n);
    } else if (kind === 'quantity') {
      const t = buildQuantityTable(rng);
      table = t;
      questions = genQuantityQuestions(rng, t, n);
    } else if (kind === 'transit') {
      const t = buildTransitTable(rng);
      table = t;
      questions = genTransitQuestions(rng, t, n);
    } else if (kind === 'program-schedule') {
      const t = buildProgramScheduleTable(rng);
      table = t;
      questions = genProgramQuestions(rng, t, n);
    } else if (kind === 'fareMatrix') {
      const t = buildFareMatrixTable(rng);
      table = t;
      questions = genFareQuestions(rng, t, n);
    } else {
      const t = buildScheduleTable(rng);
      table = t;
      questions = genScheduleQuestions(rng, t, n);
    }
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      table,
      questions,
    };
  });

  return {
    id: opts.id ?? `stats-table-${opts.seed}`,
    title: opts.title ?? `統計表考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 統計圖表(表格判讀)',
    sections,
  };
}

/** 一份考卷的總分(Σ pointsEach×題數)。 */
export function examTotalPoints(exam: StatsTableExam): number {
  return exam.sections.reduce((sum, s) => sum + s.pointsEach * s.questions.length, 0);
}

// ───────────────────────────── 反算(共用:validator + 計分共用)─────────────────────────────

/**
 * 依一題的 `verify` 規格,從**所屬表資料**重算期望答案字串。
 * validator 與線上批改共用此函式(單一真相)。表 / 規格不匹配回 null(視為壞題)。
 */
export function recomputeAnswer(
  table: StatsTableData,
  v: StatsVerify,
): string | null {
  switch (v.op) {
    case 'lookup-price': {
      if (table.kind !== 'price') return null;
      const r = table.rows[v.row];
      if (!r) return null;
      return priceDisplay(r.value, table.unit);
    }
    case 'sum-price': {
      if (table.kind !== 'price') return null;
      if (v.rows.some((i) => !table.rows[i])) return null;
      const total = v.rows.reduce((s, i) => s + table.rows[i].value, 0);
      return `${total} ${table.unit}`;
    }
    case 'diff-price': {
      if (table.kind !== 'price') return null;
      const a = table.rows[v.a];
      const b = table.rows[v.b];
      if (!a || !b) return null;
      return `${Math.abs(a.value - b.value)} ${table.unit}`;
    }
    case 'lookup-menu': {
      if (table.kind !== 'menu') return null;
      const rows = v.group === 'A' ? table.groupA : table.groupB;
      const r = rows[v.row];
      if (!r) return null;
      return `${r.value} ${table.unit}`;
    }
    case 'combo-menu': {
      if (table.kind !== 'menu') return null;
      const a = table.groupA[v.a];
      const b = table.groupB[v.b];
      if (!a || !b) return null;
      return `${a.value + b.value} ${table.unit}`;
    }
    case 'change-menu': {
      if (table.kind !== 'menu') return null;
      const a = table.groupA[v.a];
      const b = table.groupB[v.b];
      if (!a || !b) return null;
      return `${v.pay - (a.value + b.value)} ${table.unit}`;
    }
    case 'max-menu': {
      if (table.kind !== 'menu') return null;
      const rows = v.group === 'A' ? table.groupA : table.groupB;
      return maxName(rows);
    }
    case 'equal-menu': {
      if (table.kind !== 'menu') return null;
      const rows = v.group === 'A' ? table.groupA : table.groupB;
      const names = equalNames(rows);
      return names || null;
    }
    case 'lookup-cell': {
      if (table.kind !== 'schedule') return null;
      const row = table.grid[v.period];
      if (!row || row[v.day] === undefined) return null;
      return row[v.day];
    }
    case 'count-subject': {
      if (table.kind !== 'schedule') return null;
      return `${countSubject(table, v.subject)} 節`;
    }
    case 'has-subject': {
      if (table.kind !== 'schedule') return null;
      const has = table.grid.some((row) => row[v.day] === v.subject);
      return has ? '有' : '沒有';
    }
    case 'lookup-qty': {
      if (table.kind !== 'quantity') return null;
      if (table.counts[v.col] === undefined) return null;
      return `${table.counts[v.col]} ${table.unit}`;
    }
    case 'extreme-qty': {
      if (table.kind !== 'quantity') return null;
      return extremeCategory(table, v.which);
    }
    case 'diff-qty': {
      if (table.kind !== 'quantity') return null;
      const a = table.counts[v.a];
      const b = table.counts[v.b];
      if (a === undefined || b === undefined) return null;
      return `${Math.abs(a - b)} ${table.unit}`;
    }
    case 'sum-qty': {
      if (table.kind !== 'quantity') return null;
      const total = table.counts.reduce((s, c) => s + c, 0);
      return `${total} ${table.unit}`;
    }
    case 'lookup-transit': {
      if (table.kind !== 'transit') return null;
      const row = table.grid[v.train];
      if (!row || row[v.station] === undefined) return null;
      const cell = row[v.station];
      return cell === null ? '—' : formatHM(cell);
    }
    case 'elapsed-transit': {
      if (table.kind !== 'transit') return null;
      const row = table.grid[v.train];
      if (!row) return null;
      const ta = row[v.a];
      const tb = row[v.b];
      if (ta === null || tb === null || ta === undefined || tb === undefined) return null;
      const diff = Math.abs(tb - ta);
      return formatElapsed(diff);
    }
    case 'train-before': {
      if (table.kind !== 'transit') return null;
      return trainBefore(table, v.station, v.cutoff);
    }
    case 'lookup-program': {
      if (table.kind !== 'program-schedule') return null;
      if (table.programs[v.row] === undefined) return null;
      return table.programs[v.row];
    }
    case 'duration-program': {
      if (table.kind !== 'program-schedule') return null;
      const t0 = table.times[v.row];
      const t1 = table.times[v.row + 1];
      if (t0 === undefined || t1 === undefined) return null;
      return formatElapsed(t1 - t0);
    }
    case 'lookup-fare': {
      if (table.kind !== 'fareMatrix') return null;
      const row = table.matrix[v.a];
      if (!row || row[v.b] === undefined) return null;
      return `${row[v.b]} ${table.unit}`;
    }
    case 'diff-fare': {
      if (table.kind !== 'fareMatrix') return null;
      const rowAB = table.matrix[v.a];
      const rowCD = table.matrix[v.c];
      if (!rowAB || !rowCD || rowAB[v.b] === undefined || rowCD[v.d] === undefined) return null;
      return `${Math.abs(rowAB[v.b] - rowCD[v.d])} ${table.unit}`;
    }
  }
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)統計表考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt 非空、每大題至少一題、表資料形狀正確。
 *   - 表內無重複矛盾:票價 / 目錄品項名不重複;功課表 grid 維度正確。
 *   - 價格 / 金額非負;找零題確保夠付(change ≥ 0)。
 *   - **每題 answer == recomputeAnswer(table, verify)**(從表反算一致)。
 */
export function validateStatsTableExam(exam: StatsTableExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.questions.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    validateTable(section.table, section.label, errors);

    for (const q of section.questions) {
      if (seenIds.has(q.id)) errors.push(`${q.id}: 重複的題 id`);
      seenIds.add(q.id);
      if (!q.prompt || q.prompt.trim() === '') {
        errors.push(`${q.id}: prompt 為空`);
        continue;
      }
      if (!q.answer || q.answer.trim() === '') {
        errors.push(`${q.id}: answer 為空`);
        continue;
      }
      const expected = recomputeAnswer(section.table, q.verify);
      if (expected === null) {
        errors.push(`${q.id}: verify 規格與表資料不匹配(${q.verify.op})`);
        continue;
      }
      if (expected !== q.answer) {
        errors.push(`${q.id}: 反算「${expected}」≠ answer「${q.answer}」(${q.verify.op})`);
      }
      // 找零題額外硬規則:確保夠付(change ≥ 0)。
      if (q.verify.op === 'change-menu' && section.table.kind === 'menu') {
        const a = section.table.groupA[q.verify.a];
        const b = section.table.groupB[q.verify.b];
        if (a && b && q.verify.pay < a.value + b.value) {
          errors.push(`${q.id}: 找零題付款 ${q.verify.pay} < 花費 ${a.value + b.value}(不夠付)`);
        }
      }
      // 時刻表經過時間硬規則:兩站都要有停、時間相減非負(不允許過站不停的站做計算)。
      if (q.verify.op === 'elapsed-transit' && section.table.kind === 'transit') {
        const row = section.table.grid[q.verify.train];
        const ta = row?.[q.verify.a];
        const tb = row?.[q.verify.b];
        if (ta === null || tb === null || ta === undefined || tb === undefined) {
          errors.push(`${q.id}: 時刻表經過時間題用到過站不停的站`);
        } else if (tb - ta < 0) {
          errors.push(`${q.id}: 時刻表經過時間為負(${ta}→${tb})`);
        }
      }
      // 節目時長硬規則:時長 = 下一列 − 本列,須為正(時間嚴格遞增)。
      if (q.verify.op === 'duration-program' && section.table.kind === 'program-schedule') {
        const t0 = section.table.times[q.verify.row];
        const t1 = section.table.times[q.verify.row + 1];
        if (t0 === undefined || t1 === undefined || t1 - t0 <= 0) {
          errors.push(`${q.id}: 節目時長非正(時間未嚴格遞增)`);
        }
      }
      // 二維票價貴多少硬規則:差 ≥ 0(prompt 已把貴的段放前面)。
      if (q.verify.op === 'diff-fare' && section.table.kind === 'fareMatrix') {
        const f1 = section.table.matrix[q.verify.a]?.[q.verify.b];
        const f2 = section.table.matrix[q.verify.c]?.[q.verify.d];
        if (f1 !== undefined && f2 !== undefined && f1 - f2 < 0) {
          errors.push(`${q.id}: 票價貴多少為負(${f1} − ${f2})`);
        }
      }
    }
  }

  return errors;
}

/** 一組裡有幾個品項是「最貴」(= 最大值出現次數),供 max-menu 唯一性檢查。 */
function countMax(rows: TableRow[]): number {
  if (rows.length === 0) return 0;
  const max = Math.max(...rows.map((r) => r.value));
  return rows.filter((r) => r.value === max).length;
}

/** 一組裡有幾「對」同價品項,供 equal-menu 唯一性檢查(恰 1 對才合法)。 */
function countEqualPairs(rows: TableRow[]): number {
  let pairs = 0;
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      if (rows[i].value === rows[j].value) pairs += 1;
    }
  }
  return pairs;
}

/** 驗證單張表資料的形狀 / 非負 / 不重複矛盾。 */
function validateTable(table: StatsTableData, label: string, errors: string[]): void {
  if (table.kind === 'price') {
    if (table.rows.length < 2) errors.push(`${label}: 票價表列數過少`);
    const names = new Set<string>();
    for (const r of table.rows) {
      if (r.value < 0) errors.push(`${label}: 票價 ${r.name} 為負(${r.value})`);
      if (!Number.isInteger(r.value)) errors.push(`${label}: 票價 ${r.name} 非整數`);
      if (names.has(r.name)) errors.push(`${label}: 票價表身分「${r.name}」重複`);
      names.add(r.name);
    }
  } else if (table.kind === 'menu') {
    const checkGroup = (rows: TableRow[], gname: string): void => {
      if (rows.length < 2) errors.push(`${label}: ${gname} 品項過少`);
      const names = new Set<string>();
      for (const r of rows) {
        if (r.value < 0) errors.push(`${label}: ${gname}「${r.name}」價格為負`);
        if (!Number.isInteger(r.value)) errors.push(`${label}: ${gname}「${r.name}」價格非整數`);
        if (names.has(r.name)) errors.push(`${label}: ${gname} 品項「${r.name}」重複`);
        names.add(r.name);
      }
    };
    checkGroup(table.groupA, table.groupAName);
    checkGroup(table.groupB, table.groupBName);
    // 主餐組「最貴」須唯一(max-menu 答案唯一)。
    if (countMax(table.groupA) !== 1) {
      errors.push(`${label}: ${table.groupAName} 最貴的不只一樣(max 題答案不唯一)`);
    }
    // 飲料組「一樣多」須恰一對(equal-menu 答案唯一)。
    if (countEqualPairs(table.groupB) !== 1) {
      errors.push(`${label}: ${table.groupBName} 同價的不是恰一對(equal 題答案不唯一)`);
    }
  } else if (table.kind === 'schedule') {
    // schedule:grid 維度 = periods × days,格子非空。
    if (table.grid.length !== table.periods.length) {
      errors.push(`${label}: 功課表列數與節次不符`);
    }
    for (let p = 0; p < table.grid.length; p++) {
      if (table.grid[p].length !== table.days.length) {
        errors.push(`${label}: 功課表第 ${p + 1} 列欄數與星期不符`);
      }
      for (const cell of table.grid[p]) {
        if (!cell || cell.trim() === '') errors.push(`${label}: 功課表有空格`);
      }
    }
  } else if (table.kind === 'quantity') {
    // quantity:categories 與 counts 等長;數量非負整數;彼此相異(最多/最少唯一)。
    if (table.categories.length !== table.counts.length) {
      errors.push(`${label}: 數量表類別數與數值數不符`);
    }
    if (table.categories.length < 2) errors.push(`${label}: 數量表類別過少`);
    const seen = new Set<number>();
    for (let i = 0; i < table.counts.length; i++) {
      const c = table.counts[i];
      if (c < 0) errors.push(`${label}: 數量「${table.categories[i]}」為負(${c})`);
      if (!Number.isInteger(c)) errors.push(`${label}: 數量「${table.categories[i]}」非整數`);
      if (seen.has(c)) errors.push(`${label}: 數量「${c}」重複(最多/最少不唯一)`);
      seen.add(c);
    }
  } else if (table.kind === 'transit') {
    // transit:grid 維度 = trains × stations;每車次有停的時間嚴格遞增、非負整數、同日。
    if (table.stations.length < 2) errors.push(`${label}: 時刻表站數過少`);
    for (let t = 0; t < table.grid.length; t++) {
      const row = table.grid[t];
      if (row.length !== table.stations.length) {
        errors.push(`${label}: 時刻表第 ${t + 1} 車次欄數與站數不符`);
      }
      let prev = -1;
      let stops = 0;
      for (let s = 0; s < row.length; s++) {
        const v = row[s];
        if (v === null) continue;
        stops += 1;
        if (!Number.isInteger(v) || v < 0 || v >= 24 * 60) {
          errors.push(`${label}: 時刻表第 ${t + 1} 車次「${table.stations[s]}」時間不合法(${v})`);
        }
        if (v <= prev) {
          errors.push(`${label}: 時刻表第 ${t + 1} 車次時間未嚴格遞增(${prev}→${v})`);
        }
        prev = v;
      }
      // 至少 2 個停靠站才出得了經過時間題。
      if (stops < 2) errors.push(`${label}: 時刻表第 ${t + 1} 車次停靠站不足 2 站`);
    }
  } else if (table.kind === 'program-schedule') {
    // program-schedule:times 與 programs 等長;時間嚴格遞增、非負、同日。
    if (table.times.length !== table.programs.length) {
      errors.push(`${label}: 節目單時間數與節目數不符`);
    }
    if (table.programs.length < 2) errors.push(`${label}: 節目單節目過少`);
    let prev = -1;
    for (let i = 0; i < table.times.length; i++) {
      const v = table.times[i];
      if (!Number.isInteger(v) || v < 0 || v >= 24 * 60) {
        errors.push(`${label}: 節目「${table.programs[i]}」時間不合法(${v})`);
      }
      if (v <= prev) errors.push(`${label}: 節目單時間未嚴格遞增(${prev}→${v})`);
      prev = v;
    }
  } else {
    // fareMatrix:matrix n×n、對稱、同站 = 0、非負整數。
    const n = table.stations.length;
    if (n < 2) errors.push(`${label}: 票價表站數過少`);
    if (table.matrix.length !== n) errors.push(`${label}: 票價矩陣列數與站數不符`);
    for (let i = 0; i < table.matrix.length; i++) {
      if (table.matrix[i].length !== n) {
        errors.push(`${label}: 票價矩陣第 ${i + 1} 列欄數與站數不符`);
        continue;
      }
      for (let j = 0; j < n; j++) {
        const fare = table.matrix[i][j];
        if (!Number.isInteger(fare) || fare < 0) {
          errors.push(`${label}: 票價「${table.stations[i]}→${table.stations[j]}」不合法(${fare})`);
        }
        if (i === j && fare !== 0) {
          errors.push(`${label}: 同站票價非 0(${table.stations[i]})`);
        }
        if (table.matrix[j]?.[i] !== fare) {
          errors.push(`${label}: 票價不對稱(${table.stations[i]}↔${table.stations[j]})`);
        }
      }
    }
  }
}
